import type { MainToWorkerMessage } from '../shell/protocol';
import type { TerminalHandle } from '../terminal/terminal';
import {
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  applyTheme,
  getStoredColors,
  getStoredFontSize,
  getStoredTheme,
  storeColors,
  storeFontSize,
} from './theme';
import { THEME_ORDER, type ThemeName } from './themes';

export interface UiOptions {
  terminal: TerminalHandle;
  send(message: MainToWorkerMessage): void;
  onLock?(): void;
  onLogout?(): void;
  onExportBackup?(): void;
  onImportBackup?(): void;
  onResetMachine?(): void;
  onDeleteAccount?(): void;
  onDownloadCheatsheets?(): void;
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément #${id} introuvable`);
  return element as T;
}

export function setupUi(options: UiOptions): void {
  const { terminal, send } = options;
  const { term } = terminal;
  let fontSize = getStoredFontSize();

  term.options.fontSize = fontSize;
  terminal.fit();

  // --- Copier / coller
  const copyButton = byId<HTMLButtonElement>('btn-copy');
  const updateCopyState = (): void => {
    copyButton.disabled = !term.hasSelection();
  };
  term.onSelectionChange(updateCopyState);
  updateCopyState();

  copyButton.addEventListener('click', () => {
    const selection = term.getSelection();
    if (selection) void navigator.clipboard.writeText(selection);
    term.focus();
  });

  byId<HTMLButtonElement>('btn-paste').addEventListener('click', () => {
    void pasteFromClipboard();
  });

  async function pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      if (text) send({ type: 'input', data: text });
    } catch {
      /* permission refusée : Ctrl+Maj+V reste disponible */
    }
    term.focus();
  }

  // --- Taille de police de la console (le panneau a ses propres boutons)
  const applyFontSize = (next: number): void => {
    fontSize = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, next));
    term.options.fontSize = fontSize;
    storeFontSize(fontSize);
    terminal.fit();
  };
  byId<HTMLButtonElement>('btn-font-dec').addEventListener('click', () =>
    applyFontSize(fontSize - 1),
  );
  byId<HTMLButtonElement>('btn-font-inc').addEventListener('click', () =>
    applyFontSize(fontSize + 1),
  );

  // --- Thème
  const menu = byId<HTMLDivElement>('menu');
  const updateThemeChecks = (name: ThemeName): void => {
    menu.querySelectorAll<HTMLElement>('[data-action^="theme-"]').forEach((item) => {
      item.setAttribute('aria-checked', String(item.dataset.action === `theme-${name}`));
    });
  };
  const setTheme = (name: ThemeName): void => {
    applyTheme(name, term);
    updateThemeChecks(name);
    terminal.fit();
  };
  const cycleTheme = (): void => {
    const index = THEME_ORDER.indexOf(getStoredTheme());
    setTheme(THEME_ORDER[(index + 1) % THEME_ORDER.length] ?? 'dark');
  };
  byId<HTMLButtonElement>('btn-theme').addEventListener('click', () => {
    cycleTheme();
    term.focus();
  });
  updateThemeChecks(getStoredTheme());

  // --- Couleurs de la console (invite, saisie, messages pédagogiques)
  const colorsItem = menu.querySelector<HTMLElement>('[data-action="toggle-colors"]');
  let colorsEnabled = getStoredColors();
  const applyColors = (enabled: boolean): void => {
    colorsEnabled = enabled;
    storeColors(enabled);
    colorsItem?.setAttribute('aria-checked', String(enabled));
    send({ type: 'colors', enabled });
  };
  applyColors(colorsEnabled);

  // --- Recherche (champ repliable dans la barre d'outils)
  const topbar = byId<HTMLElement>('topbar');
  const searchField = byId<HTMLDivElement>('search-field');
  const searchInput = byId<HTMLInputElement>('search-input');
  const searchButton = byId<HTMLButtonElement>('btn-search');
  const searchOpen = (): boolean => topbar.classList.contains('search-open');
  const openSearch = (): void => {
    topbar.classList.add('search-open');
    searchButton.setAttribute('aria-expanded', 'true');
    searchField.setAttribute('aria-hidden', 'false');
    searchField.inert = false;
    searchInput.focus();
    searchInput.select();
  };
  const closeSearch = (): void => {
    topbar.classList.remove('search-open');
    searchButton.setAttribute('aria-expanded', 'false');
    searchField.setAttribute('aria-hidden', 'true');
    searchField.inert = true;
    terminal.search.clearDecorations();
    term.focus();
  };
  searchButton.addEventListener('click', () => {
    if (searchOpen()) closeSearch();
    else openSearch();
  });
  byId<HTMLButtonElement>('search-next').addEventListener('click', () => {
    terminal.search.findNext(searchInput.value);
  });
  byId<HTMLButtonElement>('search-prev').addEventListener('click', () => {
    terminal.search.findPrevious(searchInput.value);
  });
  searchInput.addEventListener('input', () => {
    if (searchInput.value) terminal.search.findNext(searchInput.value, { incremental: true });
    else terminal.search.clearDecorations();
  });
  searchInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) terminal.search.findPrevious(searchInput.value);
      else terminal.search.findNext(searchInput.value);
    } else if (event.key === 'Escape') {
      closeSearch();
    }
  });

  // --- Menu
  const menuButton = byId<HTMLButtonElement>('btn-menu');
  const toggleMenu = (show?: boolean): void => {
    const next = show ?? menu.hidden;
    menu.hidden = !next;
    menuButton.setAttribute('aria-expanded', String(next));
  };
  menuButton.addEventListener('click', () => toggleMenu());
  menu.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!button) return;
    const action = button.dataset.action ?? '';
    if (action === 'clear') send({ type: 'input', data: 'clear\r' });
    else if (action === 'about') byId<HTMLDialogElement>('about-dialog').showModal();
    else if (action === 'lock') options.onLock?.();
    else if (action === 'logout') options.onLogout?.();
    else if (action === 'export-backup') options.onExportBackup?.();
    else if (action === 'import-backup') options.onImportBackup?.();
    else if (action === 'reset-machine') options.onResetMachine?.();
    else if (action === 'delete-account') options.onDeleteAccount?.();
    else if (action === 'cheatsheets') options.onDownloadCheatsheets?.();
    else if (action === 'toggle-colors') applyColors(!colorsEnabled);
    else if (action.startsWith('theme-')) setTheme(action.slice('theme-'.length) as ThemeName);
    toggleMenu(false);
    if (action.startsWith('theme-') || action === 'toggle-colors') term.focus();
  });

  // --- Fermetures globales
  document.addEventListener('click', (event) => {
    const target = event.target as Node;
    if (!menu.hidden && !menu.contains(target) && !menuButton.contains(target)) toggleMenu(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (!menu.hidden) toggleMenu(false);
      if (searchOpen()) closeSearch();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      if (searchOpen()) closeSearch();
      else openSearch();
    }
  });

  // --- Focus
  byId('terminal').addEventListener('click', () => term.focus());
  term.focus();
}
